import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { UserMinus, Mail } from "lucide-react";
import { toast } from "sonner";
import {
  removeContestMember,
  setContestMemberPaid,
  setContestMemberEmail,
} from "@/lib/contestApi";

// Host-only dialog to remove members + toggle Paid flag.
// - Paid is a lightweight Venmo/Zelle accountability checkmark (not a real
//   payment gate). Editable pre and post-lock.
// - Removing a member is pre-lock only. Backend enforces both rules.
//
// Props:
//   open, onOpenChange
//   state       - contest state (code, members[], status, name)
//   hostPid     - host participant_id
//   onChanged   - (freshState) => void, called after every successful mutation
export default function ManageMembersDialog({
  open,
  onOpenChange,
  state,
  hostPid,
  onChanged,
}) {
  const [removingId, setRemovingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [emailEditId, setEmailEditId] = useState(null);
  const [emailValue, setEmailValue] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const isLocked = state.status !== "open";
  const members = state.members || [];
  const paidCount = members.filter((m) => m.paid).length;

  const sendInvite = async (m) => {
    const clean = emailValue.trim().toLowerCase();
    if (!clean.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setSendingEmail(true);
    try {
      const res = await setContestMemberEmail(state.code, hostPid, m.id, clean);
      toast.success(
        res.email_sent
          ? `Seat linked — invite sent to ${clean}`
          : `Seat linked to ${clean}, but the invite email couldn't be sent`
      );
      setEmailEditId(null);
      setEmailValue("");
      onChanged?.(res.state);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't link that email");
    } finally {
      setSendingEmail(false);
    }
  };

  const togglePaid = async (m) => {
    setTogglingId(m.id);
    try {
      const fresh = await setContestMemberPaid(state.code, hostPid, m.id, !m.paid);
      onChanged?.(fresh);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Couldn't update paid status");
    } finally {
      setTogglingId(null);
    }
  };

  const doRemove = async (m) => {
    if (
      !window.confirm(
        `Remove ${m.username} from ${state.name}?${
          m.has_submitted ? "\n\nTheir submitted picks will also be deleted." : ""
        }\n\nThis cannot be undone.`
      )
    )
      return;
    setRemovingId(m.id);
    try {
      const fresh = await removeContestMember(state.code, hostPid, m.id);
      toast.success(`${m.username} removed`);
      onChanged?.(fresh);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Couldn't remove that member");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-pbo-cream sm:max-w-md"
        data-testid="manage-members-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-pbo-green text-xl">
            Manage members
          </DialogTitle>
          <DialogDescription className="text-pbo-ink/70">
            Tap the checkbox once each friend has Venmo&apos;d / Zelled you the
            entry. Add an email to a seat to send a sign-in invite. Remove
            anyone who joined by mistake (pre-lock only, deletes their picks
            too).
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between px-1 mb-1 text-xs">
          <span
            className="text-pbo-muted"
            data-testid="paid-summary"
          >
            <strong className="text-pbo-green font-semibold">
              {paidCount}
            </strong>{" "}
            of {members.length} paid
          </span>
          {isLocked && (
            <span className="text-[10px] uppercase tracking-wider text-pbo-muted">
              Locked — removals disabled
            </span>
          )}
        </div>

        {members.length === 0 ? (
          <div className="text-center text-pbo-muted text-sm py-6 italic">
            No members have joined yet.
          </div>
        ) : (
          <ul className="divide-y divide-pbo-border/60 max-h-96 overflow-y-auto">
            {members.map((m) => (
              <li
                key={m.id}
                className="py-2.5"
                data-testid={`manage-member-row-${m.id}`}
              >
                <div className="flex items-center gap-3">
                <label
                  className="flex items-center gap-2 cursor-pointer select-none min-w-0 flex-1"
                  title="Toggle paid status"
                >
                  <Checkbox
                    checked={!!m.paid}
                    disabled={togglingId === m.id}
                    onCheckedChange={() => togglePaid(m)}
                    className="border-pbo-green data-[state=checked]:bg-pbo-green data-[state=checked]:text-pbo-cream"
                    data-testid={`paid-toggle-${m.id}`}
                  />
                  <span className="min-w-0">
                    <span className="text-pbo-ink font-medium truncate flex items-center gap-1.5">
                      {m.username}
                      {m.is_host && (
                        <span className="text-[9px] uppercase tracking-wider font-semibold text-pbo-gold">
                          Host
                        </span>
                      )}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-pbo-muted mt-0.5 block">
                      {m.paid && (
                        <span
                          className="text-pbo-green-soft font-semibold"
                          data-testid={`paid-label-${m.id}`}
                        >
                          Paid
                        </span>
                      )}
                      {m.paid && (m.has_submitted || !m.is_host) && (
                        <span className="mx-1 opacity-40">·</span>
                      )}
                      {m.has_submitted ? "Submitted picks" : "No picks yet"}
                    </span>
                    {m.has_account && (
                      <span
                        className="text-[10px] text-pbo-green-soft normal-case tracking-normal block truncate"
                        data-testid={`member-email-${m.id}`}
                      >
                        <Mail className="w-2.5 h-2.5 inline mr-1" />
                        {m.user_email || "Linked to an account"}
                      </span>
                    )}
                  </span>
                </label>
                {!m.has_account && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEmailEditId(emailEditId === m.id ? null : m.id);
                      setEmailValue("");
                    }}
                    className="border-pbo-green/40 text-pbo-green hover:bg-pbo-green hover:text-pbo-cream h-9 px-2.5 shrink-0"
                    title="Link an email + send invite"
                    data-testid={`add-email-btn-${m.id}`}
                  >
                    <Mail className="w-3.5 h-3.5" />
                  </Button>
                )}
                {!m.is_host && (
                  <Button
                    variant="outline"
                    onClick={() => doRemove(m)}
                    disabled={isLocked || removingId === m.id}
                    className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 h-9 px-3 shrink-0"
                    data-testid={`remove-member-btn-${m.id}`}
                  >
                    {removingId === m.id ? (
                      "Removing…"
                    ) : (
                      <>
                        <UserMinus className="w-3.5 h-3.5 mr-1.5" />
                        Remove
                      </>
                    )}
                  </Button>
                )}
                </div>
                {emailEditId === m.id && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      sendInvite(m);
                    }}
                    className="flex items-center gap-2 mt-2 pl-6"
                    data-testid={`email-form-${m.id}`}
                  >
                    <Input
                      type="email"
                      autoFocus
                      placeholder="friend@email.com"
                      value={emailValue}
                      onChange={(e) => setEmailValue(e.target.value)}
                      className="h-9 bg-white border-pbo-border flex-1"
                      data-testid={`email-input-${m.id}`}
                    />
                    <Button
                      type="submit"
                      disabled={sendingEmail}
                      className="h-9 bg-pbo-green hover:bg-pbo-green-deep text-pbo-cream shrink-0"
                      data-testid={`send-invite-btn-${m.id}`}
                    >
                      {sendingEmail ? "Sending…" : "Link + invite"}
                    </Button>
                  </form>
                )}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
